import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

export type PlenaryReadOnlyFlags = {
  isContentReadOnly: boolean;
  isActionsReadOnly: boolean;
};

export type PlenaryLessonStatusContextValue = {
  isReady: boolean;
  isActive: boolean | null;
  isContentReadOnly: boolean;
  isActionsReadOnly: boolean;
  setIsActive: (value: boolean) => void;
  setReadOnlyFlags: (flags: PlenaryReadOnlyFlags) => void;
};

export const PlenaryLessonStatusContext = createContext<PlenaryLessonStatusContextValue | null>(null);

// Locked-until-known: usePlenaryStatus overrides via setReadOnlyFlags once status arrives.
const INITIAL_READ_ONLY_FLAGS: PlenaryReadOnlyFlags = {
  isContentReadOnly: true,
  isActionsReadOnly: true
};

export const PlenaryLessonStatusProvider = ({ children }: PropsWithChildren) => {
  const [isActive, setIsActiveState] = useState<boolean | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [readOnlyFlags, setReadOnlyFlagsState] = useState<PlenaryReadOnlyFlags>(INITIAL_READ_ONLY_FLAGS);

  const setIsActive = useCallback((value: boolean) => {
    setIsActiveState(value);
    setIsReady(true);
  }, []);

  const setReadOnlyFlags = useCallback((flags: PlenaryReadOnlyFlags) => {
    setReadOnlyFlagsState(prev =>
      prev.isContentReadOnly === flags.isContentReadOnly && prev.isActionsReadOnly === flags.isActionsReadOnly
        ? prev
        : flags
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      isActive,
      isReady,
      isContentReadOnly: readOnlyFlags.isContentReadOnly,
      isActionsReadOnly: readOnlyFlags.isActionsReadOnly,
      setIsActive,
      setReadOnlyFlags
    }),
    [isActive, isReady, readOnlyFlags, setIsActive, setReadOnlyFlags]
  );

  return <PlenaryLessonStatusContext.Provider value={contextValue}>{children}</PlenaryLessonStatusContext.Provider>;
};

const noop = () => undefined;

const NOOP_PLENARY_LESSON_STATUS: PlenaryLessonStatusContextValue = {
  isReady: true,
  isActive: null,
  isContentReadOnly: false,
  isActionsReadOnly: false,
  setIsActive: noop,
  setReadOnlyFlags: noop
};

export function usePlenaryLessonStatus() {
  return useContext(PlenaryLessonStatusContext) ?? NOOP_PLENARY_LESSON_STATUS;
}
